/* eslint-disable react-hooks/exhaustive-deps */
import { CSSProperties, useCallback, useEffect, useRef } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { VariableSizeList } from 'react-window';
import classNames from 'classnames';

import * as K from 'common/constants';
import { LogEntry, LogEntryEdge } from 'common/graphqlTypes';

import { getRowSizeCalculator } from 'components/search/logLineUtils';
import LogLine from 'components/search/LogLine';
import { useResizeRegistration, windowHeightState } from 'components/common/Managers/LayoutStateManager';
import { settingsState } from 'components/common/Managers/SettingsManager.state';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import { searchParamState } from 'components/common/Managers/RouteStateManager.state';
import { isLiveTailActiveState, logToInspectState } from 'components/search/SearchLogsPage/LogResultsContainer.hooks';
import EventsContainer from 'components/search/SearchLogsPage/EventsContainer';
import LogDetailsContainer from 'components/search/SearchLogsPage/LogDetailsContainer';

import 'components/search/SearchLogsPage/SearchLogsResultsArea.scss'; // needed for pinned drawer styles
import './LiveTailLogResults.scss';

const SCROLL_UP_OFFSET = 20;
const BOTTOM_OFFSET = 250;

type LiveTailLogResultsProps = {
  liveTailLogs: LogEntryEdge[];
};

function LiveTailLogResults({ liveTailLogs }: LiveTailLogResultsProps): JSX.Element {
  const scrollTopRef = useRef(0);
  const windowHeight = useRecoilValue(windowHeightState);
  const [isActive, setIsActive] = useRecoilState(isLiveTailActiveState);

  const containerRef = useRef<HTMLElement>(null);
  const variableSizeListRef = useRef<VariableSizeList>(null);
  const filter = useRecoilValue(searchParamState(K.URL_PARAM.FILTER)) || '';

  const settings = useRecoilValue(settingsState);
  const timezone = useRecoilValue(timezoneState);
  const logDetailsRef = useResizeRegistration();
  const [logToInspect, setLogToInspect] = useRecoilState(logToInspectState);

  // scroll to the bottom of live tail to create the streaming effect.
  useEffect(() => {
    if (containerRef.current) {
      const scrollBarOffset =
        containerRef.current.scrollHeight - containerRef.current.scrollTop - containerRef.current.clientHeight;
      // scrollBarOffset is 0 if the scroll bar is 'at' the bottom.
      // For a slightly better UX, we continue the streaming effect if it is 'near' the bottom
      const isNearBottom = scrollBarOffset < BOTTOM_OFFSET;

      if (isNearBottom) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight - containerRef.current.clientHeight;
      }
    }
  }, [liveTailLogs]);

  useEffect(() => {
    /**
     * Handle scrolling event on live tail.
     *
     * Pausing live tail for a small scroll up event annoys the user.
     * To avoid that, we only pause live tail if the scroll up distance exceeds some offset.
     */
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    const handleScroll = (event: any): void => {
      const target = event.target as HTMLDivElement;
      const scrollTop = target.scrollTop;
      const delta = scrollTop - scrollTopRef.current;
      const isUp = delta < 0;
      if (isUp && Math.abs(delta) > SCROLL_UP_OFFSET) {
        setIsActive(false);
      }
      scrollTopRef.current = scrollTop;
    };

    if (containerRef.current) {
      containerRef.current.addEventListener('scroll', handleScroll);
    }

    return (): void => {
      if (containerRef.current) {
        containerRef.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isActive]);

  const getEventByToken = (token: string): LogEntry | null => {
    const logEntryEdge = liveTailLogs.find((edge) => edge.continuationToken === token);
    return logEntryEdge?.logEntry || null;
  };

  const getTokenByOffset = (token: string, offset: number): string => {
    const index = liveTailLogs.findIndex((edge: LogEntryEdge) => edge.continuationToken === token);
    const logEntryEdge = liveTailLogs[index + offset];
    setLogToInspect(logEntryEdge?.logEntry);
    return logEntryEdge?.continuationToken || '';
  };

  const handleContainerClick = (token: string): void => {
    setIsActive(false);
    setLogToInspect(getEventByToken(token));
  };

  const calculateRowSize = useCallback(getRowSizeCalculator(liveTailLogs, settings), [liveTailLogs, settings]);

  const liveTailLogLine = ({ index, style }: { index: number; style: CSSProperties }): JSX.Element => {
    const { continuationToken, logEntry } = liveTailLogs[index];
    const key = continuationToken || String(index);
    const isEven = index % 2 === 0;
    const logLineClass = classNames('LogLine', {
      'LogLine--Even': isEven,
      'LogLine--Odd': !isEven,
      'LogLine--Active': logEntry === logToInspect,
    });

    return (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div style={style} className={logLineClass} key={key} data-continuation-token={continuationToken}>
        <LogLine
          index={index}
          continuationToken={continuationToken}
          logEntry={logEntry}
          filter={filter}
          settings={settings}
          timezone={timezone}
        />
      </div>
    );
  };

  // TODO: Hardcoded component measurements are not reliable {@link https://scalyr.myjetbrains.com/youtrack/issue/FRONT-4039}
  const logView = (
    <div className="LiveTail__LogResults">
      <VariableSizeList
        height={windowHeight - 120}
        width="100%"
        itemCount={liveTailLogs.length}
        itemSize={calculateRowSize}
        outerRef={containerRef}
        ref={variableSizeListRef}
      >
        {liveTailLogLine}
      </VariableSizeList>
      <div ref={logDetailsRef} data-resize-id={K.RESIZE_ID.SEARCH_LOG_DETAILS}>
        <LogDetailsContainer></LogDetailsContainer>
      </div>
    </div>
  );

  return (
    <EventsContainer
      eventsView={logView}
      getEventByToken={getEventByToken}
      getTokenByOffset={getTokenByOffset}
      outsideRefs={[logDetailsRef]}
      onClick={handleContainerClick}
      displayMode={K.DISPLAY_MODE.LINES}
    />
  );
}

export default LiveTailLogResults;
